// @flow
import type { DayOfWeek } from 'common/domain/DayOfWeek';
import type { SerializedSpotScheduleTimeSlot } from './SpotScheduleTimeSlot';
import SpotScheduleTimeSlot from './SpotScheduleTimeSlot';

type SerializedSpotsPerDay = {
  [key: DayOfWeek]: SerializedSpotScheduleTimeSlot[],
};

type SerializedSpotSchedule = {
  callSign: string;
  spotsPerDay: SerializedSpotsPerDay;
};

type SpotsPerDay = {
  [key: DayOfWeek]: SpotScheduleTimeSlot[],
};

class SpotSchedule {

  callSign: string;
  spotsPerDay: SpotsPerDay;

  constructor(callSign: string, spotsPerDay: SpotsPerDay) {
    this.callSign = callSign;
    this.spotsPerDay = spotsPerDay;
  }

  addTimeSlot(day: DayOfWeek, timeSlot: SpotScheduleTimeSlot): SpotSchedule {
    if (this.doesTimeSlotOverlapWithExistingTimeSlots(day, timeSlot)) {
      throw new Error('The given time slot conflicts with already existing time slots');
    }

    const newSpotsPerDay = {
      ...this.spotsPerDay,
      [day]: (this.spotsPerDay[day] || []).slice(),
    };

    newSpotsPerDay[day].push(timeSlot);

    return new SpotSchedule(this.callSign, newSpotsPerDay);
  }

  removeTimeSlot(day: DayOfWeek, timeSlot: SpotScheduleTimeSlot): SpotSchedule {
    const newSpotsPerDay = {
      ...this.spotsPerDay,
      [day]: this.spotsPerDay[day].filter(s => !s.is(timeSlot)),
    };

    return new SpotSchedule(this.callSign, newSpotsPerDay);
  }

  updateTimeSlot(day: DayOfWeek, oldSpotsTimeSlot: SpotScheduleTimeSlot, newSpotsTimeSlot: SpotScheduleTimeSlot): SpotSchedule {
    const scheduleWithRemovedTimeSlot = this.removeTimeSlot(day, oldSpotsTimeSlot);
    const scheduleWithNewTimeSlot = scheduleWithRemovedTimeSlot.addTimeSlot(day, newSpotsTimeSlot);

    return scheduleWithNewTimeSlot;
  }

  doesTimeSlotOverlapWithExistingTimeSlots(day: DayOfWeek, timeSlot: SpotScheduleTimeSlot): boolean {
    const spotsInDay = this.spotsPerDay[day] || [];
    const numberOfOverlappingTimeSlots = spotsInDay.filter(s => s.overlaps(timeSlot)).length;

    return numberOfOverlappingTimeSlots > 0;
  }

  toObject(): SerializedSpotSchedule {
    const spotsPerDay: SerializedSpotsPerDay = Object.entries(this.spotsPerDay).reduce((acc, entry: any) => {
      const day = entry[0];
      const spotsScheduleTimeSlots = entry[1];
      const serializedSpotScheduleTimeSlots = spotsScheduleTimeSlots.map(s => s.toObject());

      acc[day] = serializedSpotScheduleTimeSlots;

      return acc;
    }, {});

    return {
      callSign: this.callSign,
      spotsPerDay,
    };
  }

  static fromObject(object: SerializedSpotSchedule): SpotSchedule {
    const callSign = object.callSign;
    const spotsPerDay = Object.entries(object.spotsPerDay).reduce((acc, entry: any) => {
      const day = entry[0];
      const serializedSpotScheduleTimeSlots = entry[1];
      const spotsScheduleTimeSlots = serializedSpotScheduleTimeSlots.map(SpotScheduleTimeSlot.fromObject);

      acc[day] = spotsScheduleTimeSlots;

      return acc;
    }, {});

    return new SpotSchedule(callSign, spotsPerDay);
  }
}

export default SpotSchedule;



// WEBPACK FOOTER //
// ./src/statsradio/modules/salesInventory/domain/SpotSchedule.js